import { validateWeightingFactorsFormat } from 'helper/flux/weightingFactors';
import {
  MENSAGEM_ENVIO_NAO_CONFIRMADO,
  ehFalhaTecnica,
  ehRecusaDaValidacao,
  ehValidacaoIndisponivel,
  extrairMensagemRecusa,
} from 'helper/flux/mensagemRecusa';
import { VerifyZipParams } from 'helper/products/flux';
import useFluxFileUploadForm from 'utils/hooks/flux/fileUploadForm';
import useFluxFileUpload from 'utils/hooks/flux/fileUpload';
import useVerifyZipFile from 'utils/hooks/flux/verifyZipFile';
import useVerifyZipFileModal from 'utils/hooks/flux/verifyZipFileModal';
import useGetCompany from 'utils/hooks/companies/useGetCompany';
import useGetUser from 'utils/hooks/users/useGetUser';

const LABEL_VAZAO_MEDIA_PONDERADA = 'Produzir vazão média ponderada';

// Validador do ZIP fora do ar no servidor (code 503): a falha não é do arquivo.
const TITULO_VALIDACAO_INDISPONIVEL = 'Validação indisponível';

/**
 * Estado + ações do formulário de upload de estudo, compartilhados entre a versão desktop
 * (sidebar) e a mobile (aba "Novo Estudo"). Mantém `fileRef` e `reset` na mesma instância de
 * `useFluxFileUploadForm` — chamar o hook de formulário duas vezes criaria refs diferentes e o
 * `reset` deixaria de limpar o `<input type="file">`.
 */
export default function useFluxUploadFormActions() {
  const form = useFluxFileUploadForm();
  const {
    name, file: zipFile, filename, setFilename, setFile,
    startDate, endDate, multiScenarios, weightingFactorsText, reset, fileRef,
  } = form;

  const userData = useGetUser();
  const companyData = useGetCompany(userData?.id_empresa);
  const { verifyZip, isLoading: isVerifying } = useVerifyZipFile();
  const {
    modalTitle, setTitle,
    modalBody, setBody,
    modalHeaderClass, setHeaderClass,
    showModal, setShowModal,
  } = useVerifyZipFileModal();
  const {
    uploadStudies, uploadStudiesParams, isLoading: isUploadLoading,
  } = useFluxFileUpload();

  const fileNotReady = !userData || !companyData || !filename || !zipFile || !startDate || !endDate;

  // Descarta só o ZIP (input + redux), mantendo o resto do formulário. O
  // <input type=file> não dispara change quando o usuário escolhe de novo o MESMO
  // arquivo: sem zerar o value, o ZIP corrigido com o mesmo nome não seria lido e
  // o próximo envio mandaria o base64 antigo.
  const descartarArquivo = () => {
    if (fileRef.current) {
      fileRef.current.value = '';
    }
    setFile('');
    setFilename('');
  };

  const handleFileInput = (event: any) => {
    const file = event.target.files?.[0];
    if (file) {
      setFilename(file.name);
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        if (reader.result) {
          setFile(reader.result.toString().split(',')[1]);
        }
      };
    } else {
      // Seletor cancelado (o Chrome dispara change com a lista vazia): o input
      // fica vazio, e o redux também, para não seguir enviando o ZIP anterior.
      setFile('');
      setFilename('');
    }
  };

  const verifyZipFile = async () => {
    if (fileNotReady) {
      return;
    }
    const params: VerifyZipParams = {
      user: userData.username,
      empresa: companyData.apelido,
      nome_estudo: name,
      options: '',
      data_inicial: startDate,
      data_final: endDate,
      file_name: filename,
      file_base64: zipFile,
    };
    const response = await verifyZip(params);
    if (response.isSuccess) {
      setTitle(response.data.title);
      setBody(response.data.message);
      setHeaderClass('bg-success');
      setShowModal(true);
    } else {
      // Recusa do validador: HTTP 200 com code 400 e message '[<motivo>]',
      // que o normalizeResult (services/api/base.tsx) transforma em erro. Outras
      // falhas (rede, 403, 500, validador indisponível) não culpam o arquivo.
      if (ehValidacaoIndisponivel(response.error)) {
        setTitle(TITULO_VALIDACAO_INDISPONIVEL);
      } else {
        setTitle(ehRecusaDaValidacao(response.error) ? 'Arquivo com problemas' : 'Não foi possível verificar');
      }
      setBody(extrairMensagemRecusa(response.error));
      setHeaderClass('bg-danger');
      setShowModal(true);
    }
  };

  const submitStudy = async () => {
    if (multiScenarios.includes(LABEL_VAZAO_MEDIA_PONDERADA)) {
      const result = validateWeightingFactorsFormat(weightingFactorsText);
      if (!result.valid && 'message' in result) {
        setTitle('Fatores de ponderação inválidos');
        setBody(result.message);
        setHeaderClass('bg-danger');
        setShowModal(true);
        return;
      }
    }

    await uploadStudies(uploadStudiesParams)
      .unwrap()
      .then((response) => {
        if (response.code === 200) {
          setTitle('');
          setBody('Seu estudo foi enviado com sucesso!');
          setHeaderClass('bg-success');
          setShowModal(true);
          reset();
        }
      })
      .catch((uploadError) => {
        // Sem reset(): o formulário fica preenchido para o usuário corrigir e reenviar.
        if (ehValidacaoIndisponivel(uploadError)) {
          // O validador caiu no servidor antes de gravar: nada foi enfileirado e o
          // time já foi alertado. O ZIP também fica, para tentar de novo mais tarde.
          setTitle(TITULO_VALIDACAO_INDISPONIVEL);
          setBody(extrairMensagemRecusa(uploadError));
        } else {
          // Só o ZIP sai, para o arquivo corrigido ser lido de novo.
          descartarArquivo();
          if (ehFalhaTecnica(uploadError)) {
            setTitle('Envio não confirmado');
            setBody(MENSAGEM_ENVIO_NAO_CONFIRMADO);
          } else {
            setTitle('Estudo não enviado');
            setBody(extrairMensagemRecusa(uploadError));
          }
        }
        setHeaderClass('bg-danger');
        setShowModal(true);
      });
  };

  return {
    form,
    fileNotReady,
    handleFileInput,
    verifyZipFile,
    submitStudy,
    isVerifying,
    isUploadLoading,
    modalTitle,
    modalBody,
    modalHeaderClass,
    showModal,
    setShowModal,
  };
}
